// client/src/coms/settings/useDeviceSettings.js
// Microphone / speaker / camera choices and the noise gate (all saved in this browser), with the device list.
// Device settings: choices are written to local storage and announced to the
// voice client the moment they change; a device that disappears falls back to the system default.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getPreferredInputDeviceId,
  getPreferredOutputDeviceId,
  getPreferredVoiceOutputDeviceId,
  setPreferredInputDeviceId,
  setPreferredOutputDeviceId,
  setPreferredVoiceOutputDeviceId,
  emitAudioDevicePrefsChanged,
} from '../audio/audioDevicePrefs';
import { getNoiseGateThresholdDb, setNoiseGateThresholdDb, emitNoiseGatePrefsChanged } from '../voip/noiseGatePrefs';
import { getPreferredVideoInputDeviceId, setPreferredVideoInputDeviceId, emitVideoDevicePrefsChanged } from '../video/videoDevicePrefs';

export const DEFAULT_GATE_DB = -45; // getNoiseGateThresholdDb()'s default

export function canSetSinkId() {
  try {
    return typeof HTMLMediaElement !== 'undefined' && typeof HTMLMediaElement.prototype.setSinkId === 'function';
  } catch {
    return false;
  }
}

function normalizeKind(k) {
  const s = String(k || '').toLowerCase().trim();
  return s === 'audioinput' || s === 'audiooutput' || s === 'videoinput' ? s : 'unknown';
}

export function buildDeviceOptions(devices, kind) {
  const k = normalizeKind(kind);
  const opts = [];
  for (const d of devices || []) {
    if (!d || normalizeKind(d.kind) !== k) continue;
    const id = String(d.deviceId || '').trim();
    // "default" / "communications" are the browser's aliases for the system choices, already covered by "System default".
    if (!id || id === 'default' || id === 'communications') continue;
    opts.push({ id, label: String(d.label || '').trim() || `${k === 'videoinput' ? 'Camera' : k === 'audioinput' ? 'Microphone' : 'Output'} ${opts.length + 1}` });
  }
  opts.sort((a, b) => a.label.localeCompare(b.label));
  return opts;
}

async function requestMediaPermission(kind) {
  if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) return { ok: false, error: 'This browser cannot access microphones or cameras.' };
  try {
    const stream = await navigator.mediaDevices.getUserMedia(kind === 'audio' ? { audio: true } : { video: true });
    stream.getTracks().forEach((t) => {
      try {
        t.stop();
      } catch {}
    });
    return { ok: true, error: '' };
  } catch (e) {
    const name = e && e.name;
    const what = kind === 'audio' ? 'microphone' : 'camera';
    if (name === 'NotAllowedError') return { ok: false, error: `Access to your ${what} is blocked. Allow it from the padlock in your browser’s address bar, then try again.` };
    if (name === 'NotFoundError') return { ok: false, error: `No ${what} was found. Check that it is plugged in.` };
    return { ok: false, error: String((e && e.message) || e || `Could not open the ${what}.`) };
  }
}

export default function useDeviceSettings() {
  const [loading, setLoading] = useState(false);
  const [deviceError, setDeviceError] = useState('');
  const [permissionError, setPermissionError] = useState('');
  const [devices, setDevices] = useState([]);
  const [counts, setCounts] = useState({ audioinput: 0, audiooutput: 0, videoinput: 0 });

  const [inputId, setInputId] = useState(() => String(getPreferredInputDeviceId() || '').trim());
  const [outputId, setOutputId] = useState(() => String(getPreferredOutputDeviceId() || '').trim());
  // Legacy: a separate "voice output" was stored but never used by the voice client; it is kept in step with Output.
  const [voiceOutputId, setVoiceOutputId] = useState(() => String(getPreferredVoiceOutputDeviceId() || '').trim());
  const [videoInputId, setVideoInputId] = useState(() => String(getPreferredVideoInputDeviceId() || '').trim());
  const [gateDb, setGateDb] = useState(() => getNoiseGateThresholdDb());

  const mountedRef = useRef(false);

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    const next = { inputId: String(inputId || '').trim(), outputId: String(outputId || '').trim(), voiceOutputId: String(voiceOutputId || '').trim() };
    if (
      String(getPreferredInputDeviceId() || '').trim() === next.inputId &&
      String(getPreferredOutputDeviceId() || '').trim() === next.outputId &&
      String(getPreferredVoiceOutputDeviceId() || '').trim() === next.voiceOutputId
    ) {
      return;
    }
    setPreferredInputDeviceId(next.inputId);
    setPreferredOutputDeviceId(next.outputId);
    setPreferredVoiceOutputDeviceId(next.voiceOutputId);
    emitAudioDevicePrefsChanged(next);
  }, [inputId, outputId, voiceOutputId]);

  useEffect(() => {
    setPreferredVideoInputDeviceId(videoInputId);
    emitVideoDevicePrefsChanged({ videoInputId });
  }, [videoInputId]);

  useEffect(() => {
    setNoiseGateThresholdDb(gateDb);
    emitNoiseGatePrefsChanged({ thresholdDb: gateDb });
  }, [gateDb]);

  const inputOptions = useMemo(() => buildDeviceOptions(devices, 'audioinput'), [devices]);
  const outputOptions = useMemo(() => buildDeviceOptions(devices, 'audiooutput'), [devices]);
  const videoOptions = useMemo(() => buildDeviceOptions(devices, 'videoinput'), [devices]);

  // A saved device is only dropped once the browser has really listed devices of that kind without it. Before the
  // list loads, or while the site has no microphone/camera permission (ids are hidden then), the choice is kept:
  // the old code reset it in both cases, so merely opening Settings could forget your microphone.
  const keep = (id, options) => !id || !options.length || options.some((o) => o.id === id);
  const selectedInputId = keep(inputId, inputOptions) ? inputId : '';
  const selectedOutputId = keep(outputId, outputOptions) ? outputId : '';
  const selectedVideoId = keep(videoInputId, videoOptions) ? videoInputId : '';

  useEffect(() => {
    if (inputId && !selectedInputId) setInputId('');
  }, [inputId, selectedInputId]);
  useEffect(() => {
    if (outputId && !selectedOutputId) {
      setOutputId('');
      setVoiceOutputId('');
    }
  }, [outputId, selectedOutputId]);
  useEffect(() => {
    if (videoInputId && !selectedVideoId) setVideoInputId('');
  }, [videoInputId, selectedVideoId]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setDeviceError('');
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
        setDeviceError('This browser cannot list audio and video devices.');
        return;
      }
      const list = await navigator.mediaDevices.enumerateDevices();
      setDevices(list || []);
      const c = { audioinput: 0, audiooutput: 0, videoinput: 0 };
      for (const d of list || []) {
        const k = normalizeKind(d && d.kind);
        if (k in c) c[k] += 1;
      }
      setCounts(c);
    } catch (e) {
      setDeviceError(String((e && e.message) || e || 'Could not list your devices.'));
    } finally {
      setLoading(false);
    }
  }, []);

  const requestPermission = useCallback(
    async (kind) => {
      setPermissionError('');
      setLoading(true);
      try {
        const r = await requestMediaPermission(kind);
        if (!r.ok && r.error) setPermissionError(r.error);
      } finally {
        setLoading(false);
      }
      await refresh();
    },
    [refresh]
  );

  useEffect(() => {
    refresh();
    const onChange = () => refresh();
    try {
      navigator.mediaDevices && navigator.mediaDevices.addEventListener && navigator.mediaDevices.addEventListener('devicechange', onChange);
    } catch {}
    return () => {
      try {
        navigator.mediaDevices && navigator.mediaDevices.removeEventListener && navigator.mediaDevices.removeEventListener('devicechange', onChange);
      } catch {}
    };
  }, [refresh]);

  // Labels are empty until the site may use a microphone/camera.
  const needsMicPermission = inputOptions.length > 0 && devices.filter((d) => normalizeKind(d.kind) === 'audioinput').every((d) => !d.label);
  const needsCameraPermission = videoOptions.length > 0 && devices.filter((d) => normalizeKind(d.kind) === 'videoinput').every((d) => !d.label);

  return {
    loading,
    deviceError,
    permissionError,
    counts,
    inputOptions,
    outputOptions,
    videoOptions,
    inputId: selectedInputId,
    outputId: selectedOutputId,
    videoInputId: selectedVideoId,
    setInputId: (v) => setInputId(String(v || '')),
    setOutputId: (v) => {
      setOutputId(String(v || ''));
      setVoiceOutputId(String(v || ''));
    },
    setVideoInputId: (v) => setVideoInputId(String(v || '')),
    gateDb,
    setGateDb,
    refresh,
    requestPermission,
    needsMicPermission,
    needsCameraPermission,
    hasGetUserMedia: !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia),
    hasEnumerate: !!(navigator.mediaDevices && navigator.mediaDevices.enumerateDevices),
  };
}
